/**
 * Request Abort Manager - Shared utility for managing request abortion across micro-frontends
 *
 * This class manages abortion of pending API requests, through axios interceptors
 * Instances across different micro-frontends are managed through event-driven requests.
 * It allows for aborting requests by URL, HTTP method, or pillar name.
 *
 */
class RequestAbortManager {
    pendingRequests = new Map();
    static globalAbortEvent = 'global-abort-all-requests';
    static hasAbortAny = AbortSignal?.any !== undefined;
    globalAbortController = new AbortController();
    blacklist;
    blacklistKeys;
    constructor(blacklist = {}) {
        this.blacklist = blacklist;
        this.blacklistKeys = Object.keys(blacklist); // Cache keys once at construction
        // Listen for global abort events from other AbortManager instances
        window?.addEventListener(RequestAbortManager.globalAbortEvent, () => {
            this.abortAllRequestsLocal();
        });
        // Listen for URL-specific abort events using a dedicated event type
        window?.addEventListener('abort-url-requests', (event) => {
            const customEvent = event;
            if (customEvent.detail?.url) {
                this.abortRequestsByUrlLocal(customEvent.detail.url, customEvent.detail.method);
            }
        });
    }
    /**
     * Checks if a request should be exempt from cancellation based on the blacklist
     * @param url The request URL to check against blacklist patterns
     * @param method HTTP method (GET, POST, etc.)
     * @returns True if the request should be protected from abortion, false otherwise
     * @example
     * // Blacklist specific methods for a URL:
     * { '/api/auth': ['POST', 'PUT'] }
     *
     * // Blacklist all methods for a URL (use empty array):
     * { '/api/critical': [] }
     */
    isBlacklisted(url, method) {
        // Early return if blacklist is empty - no point in checking further
        if (this.blacklistKeys.length === 0) {
            return false;
        }
        const normalizedMethod = method.toUpperCase();
        // Check for exact URL match first
        if (this.blacklist[url]) {
            const allowedMethods = this.blacklist[url];
            // Empty array means all methods are blacklisted
            return allowedMethods.length === 0 || allowedMethods.includes(normalizedMethod);
        }
        // Fallback: Check if URL starts with any blacklist pattern
        const matchingKey = this.blacklistKeys.find((key) => url.startsWith(key));
        if (!matchingKey) {
            return false;
        }
        const allowedMethods = this.blacklist[matchingKey];
        // If the array is empty, it means all methods are blacklisted for this URL
        if (allowedMethods.length === 0) {
            return true;
        }
        // Check if the specific method is blacklisted for this URL pattern
        return allowedMethods.includes(normalizedMethod);
    }
    /**
     * Gets the URL event name
     * @param url URL to generate event name for
     * @param method Optional HTTP method
     * @returns URL event name
     */
    getUrlEventName(url, method) {
        const methodPrefix = method ? `${method.toUpperCase()}-` : '';
        const encodedUrl = encodeURIComponent(url);
        return `abort-${methodPrefix}${encodedUrl}-requests`;
    }
    /**
     * Creates an AbortController for a request and tracks it
     * @param requestId Unique identifier for the request
     * @returns AbortController instance
     */
    createAbortController(requestId) {
        const controller = new AbortController();
        this.pendingRequests.set(requestId, controller);
        return controller;
    }
    /**
     * Removes a request from tracking (called when request completes)
     * @param requestId Unique identifier for the request
     */
    removeRequest(requestId) {
        this.pendingRequests.delete(requestId);
    }
    /**
     * Handles aborting a request by its controller
     * @param controller AbortController instance for the request
     * @param requestId Unique identifier for the request
     */
    handleAbort(controller, requestId) {
        try {
            controller.abort();
        }
        catch (error) {
            console.warn(`Failed to abort request ${requestId}:`, error);
        }
    }
    /**
     * Aborts all pending requests locally (called by global event)
     */
    abortAllRequestsLocal() {
        // Use AbortSignal.any if available, otherwise abort all pending requests manually
        if (RequestAbortManager.hasAbortAny) {
            this.globalAbortController.abort();
            this.globalAbortController = new AbortController();
        }
        else {
            this.pendingRequests.forEach(this.handleAbort);
        }
        this.pendingRequests.clear();
    }
    /**
     * Aborts all requests that match the specified URL and method locally
     * @param url URL to match requests against
     * @param method Optional HTTP method to filter requests (e.g., 'GET', 'POST')
     */
    abortRequestsByUrlLocal(url, method) {
        // Abort all requests that match the specified URL and method
        this.pendingRequests.forEach((controller, requestId) => {
            if (requestId.includes(url) && (method ? requestId.startsWith(method) : true)) {
                this.handleAbort(controller, requestId);
                this.pendingRequests.delete(requestId);
            }
        });
    }
    /**
     * Aborts all requests that match the specified URL and method across all instances
     * @param url URL to match requests against
     * @param method Optional HTTP method to filter requests (e.g., 'GET', 'POST')
     */
    abortRequestsByUrl(url, method) {
        // First abort local requests
        this.abortRequestsByUrlLocal(url, method);
        // Then broadcast to all other AbortManager instances
        try {
            window?.dispatchEvent(new CustomEvent('abort-url-requests', {
                detail: { url, method },
            }));
        }
        catch (error) {
            // Silently handle cases where window is not available (e.g., SSR, tests)
            console.debug('Could not dispatch URL abort event:', error);
        }
    }
    /**
     * Aborts all pending requests
     */
    abortAllRequests() {
        // Broadcast abort event
        try {
            window?.dispatchEvent(new CustomEvent(RequestAbortManager.globalAbortEvent));
        }
        catch (error) {
            // Silently handle cases where window is not available (e.g., SSR, tests)
            console.debug('Could not dispatch global abort event:', error);
        }
    }
    /**
     * Generates a unique request ID
     * @param method HTTP method
     * @param url Request URL
     * @returns Unique request identifier
     */
    generateRequestId(method, url) {
        return `${method}-${url}-${new Date().getTime()}`;
    }
    /**
     * Checks if an error is from request abortion
     * @param error The error to check
     * @returns True if error is from request abortion
     */
    isAbortError(error) {
        if (error) {
            const { name, code } = error;
            return name === 'AbortError' || code === 'ERR_CANCELED';
        }
        return false;
    }
    /**
     * Request interceptor function to add abort controller to requests
     * @param config Axios request config
     * @returns Modified request config
     */
    requestInterceptor = (config) => {
        // Check if the request is blacklisted - if so, return config unchanged
        if (config.url && config.method && this.isBlacklisted(config.url, config.method)) {
            return config;
        }
        // Create abort controller for this request to handle location changes
        const requestId = this.generateRequestId(config.method, config.url);
        const abortController = this.createAbortController(requestId);
        // If AbortSignal.any is available, use it to combine signals
        // Otherwise, use the single abort controller signal as fallback
        const signal = RequestAbortManager.hasAbortAny
            ? AbortSignal.any([abortController.signal, this.globalAbortController.signal])
            : abortController.signal;
        config.signal = signal;
        // Store the request ID in the config for later reference
        config.__requestId = requestId;
        return config;
    };
    /**
     * Response success interceptor function to clean up completed requests
     * @param response Axios response
     * @returns Unmodified response
     */
    responseSuccessInterceptor = (response) => {
        // Clean up completed request from abort manager
        const requestId = response.config?.__requestId;
        if (requestId) {
            this.removeRequest(requestId);
        }
        return response;
    };
    /**
     * Response error interceptor function to clean up failed requests and handle aborts
     * @param error Axios error
     * @returns Rejected promise
     */
    responseErrorInterceptor = (error) => {
        // Clean up failed/aborted request from abort manager
        const requestId = error.config?.__requestId;
        if (requestId) {
            this.removeRequest(requestId);
        }
        // If the error is an abort error, resolve it to avoid unhandled rejections
        if (this.isAbortError(error)) {
            return Promise.reject({
                ...error,
                isAbortError: true,
            });
        }
        return Promise.reject(error);
    };
}
export default RequestAbortManager;
